/**
 * Offline outbox for chat messages.
 *
 * Send is promoted into this IndexedDB queue before the composer draft is
 * cleared. A reload/crash can therefore only leave the text/files in one of
 * two durable homes: the UI draft (pre-promotion) or this outbox
 * (post-promotion).
 *
 * Drained messages keep their relative order. The outbox id is also sent as
 * `client_id`, which the backend stores as `messages.external_message_id`, so
 * replay after a reload is idempotent instead of duplicating messages.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

interface OutboxSchema extends DBSchema {
  pending: {
    key: string
    value: OutboxItem
    indexes: { 'by_chat': string; 'by_created': number }
  }
}

const DB_NAME = 'lane-control-outbox'
const DB_VERSION = 1
export const OUTBOX_SYNC_TAG = 'agentchat-message-outbox'
export const OUTBOX_DRAIN_LOCK = 'agentchat-message-outbox-drain'
const OUTBOX_DELIVERY_TIMEOUT_MS = 15_000
/** Mirrors `MAX_BYTES` in scripts/pwa_backend/routers/attachments.py. A file
 * over the cap is refused before any byte is sent: uploading it could only
 * end in HTTP 413 after spending mobile data. */
export const MAX_UPLOAD_BYTES = 1024 * 1024 * 1024
/** Attachment uploads have no total deadline — a 3-hour recording can need
 * minutes on mobile data. An upload is abandoned (and the next drain resumes
 * from the bytes the server holds) only once no bytes have moved for this
 * long. */
const UPLOAD_STALL_TIMEOUT_MS = 60_000
/** Resumable uploads PUT raw bytes in chunks of this size. */
const RESUMABLE_CHUNK_BYTES = 8 * 1024 * 1024
let _dbPromise: Promise<IDBPDatabase<OutboxSchema>> | null = null
let _drainPromise: Promise<OutboxDrainResult> | null = null
// Unknown on each cold page until the first drain reads IndexedDB. Thereafter
// successful API traffic can cheaply ignore the outbox when it is known empty.
let _autoDrainMayHaveWork = true
const _previewUrls = new Map<string, string>()

export type OutboxAttachment = {
  id: string
  kind: 'image' | 'video' | 'audio' | 'voice' | 'document' | 'pdf'
  filename: string | null
  mime_type: string | null
  size_bytes: number | null
  local_path?: string | null
  preview_url?: string
  file?: File | Blob | null
}

export type OutboxItem = {
  id: string
  chat_id: string
  body: string
  force: boolean
  topic_id: string | null
  model_override?: string | null
  effort_override?: string | null
  reply_to_message_id?: string | null
  attachments?: OutboxAttachment[]
  created_at: number
  attempts: number
  /** Last delivery failure. Local outbox work is not part of the server queue
   * until the POST succeeds, so the UI must never label this as Queued #N. */
  last_error?: string | null
  last_status?: number | null
  last_attempt_at?: number | null
  /** The server rejected this exact payload (4xx) and there is no stale
   * send decoration left to strip, so replaying it can never succeed.
   * Parked items are excluded from the periodic auto-drain and deliver
   * only through an explicit tap-to-retry. */
  parked?: boolean
}

export type OutboxServerReceipt = {
  ok: boolean
  message_id: string
  queued: boolean
  session_title?: string | null
}

export type OutboxDelivery = {
  client_id: string
  receipt: OutboxServerReceipt
}

export type OutboxDrainResult = {
  sent: number
  failed: number
  deliveries: OutboxDelivery[]
}

type OutboxListener = () => void
const _listeners = new Set<OutboxListener>()
let _notificationsReady = false
let _changeChannel: BroadcastChannel | null = null

function receiveOutboxChange(): void {
  // Another window may have added work after this page observed an empty queue.
  _autoDrainMayHaveWork = true
  notifyListeners(_listeners)
}

function ensureOutboxNotifications(): void {
  if (_notificationsReady || typeof window === 'undefined') return
  _notificationsReady = true
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      _changeChannel = new BroadcastChannel('agentchat-outbox-changes')
      _changeChannel.addEventListener('message', (event: MessageEvent<unknown>) => {
        if (event.data === 'changed') receiveOutboxChange()
      })
    } catch {
      // Foreground reconciliation still works when storage policy blocks IPC.
    }
  }
  if (typeof navigator !== 'undefined') {
    navigator.serviceWorker?.addEventListener?.('message', (event: MessageEvent<unknown>) => {
      const data = event.data
      if (data && typeof data === 'object'
        && 'type' in data && data.type === 'message-outbox-sent'
        && 'client_id' in data && typeof data.client_id === 'string') {
        receiveOutboxChange()
      }
    })
  }
  window.addEventListener('focus', receiveOutboxChange)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') receiveOutboxChange()
    })
  }
}

/** Notify on every outbox mutation so list filters can reflect pending
 * work immediately (a chat with an undelivered message must sort as
 * live, not finished, without waiting for a poll). */
export function subscribeOutbox(listener: OutboxListener): () => void {
  ensureOutboxNotifications()
  _listeners.add(listener)
  return () => { _listeners.delete(listener) }
}

function notifyOutboxChanged(): void {
  ensureOutboxNotifications()
  notifyListeners(_listeners)
  try {
    _changeChannel?.postMessage('changed')
  } catch {
    // A closed/unavailable channel must not turn a durable mutation into failure.
  }
}

function notifyListeners(listeners: Set<OutboxListener>): void {
  for (const listener of [...listeners]) {
    try {
      listener()
    } catch {
      // One broken subscriber must not block the others.
    }
  }
}

/** Live progress of the attachment upload an outbox item is waiting on. */
export type OutboxUploadProgress = {
  /** 1-based index of the file uploading now, out of `files`. */
  file: number
  files: number
  /** Bytes of this message's attachments already stored or sent. */
  sentBytes: number
  totalBytes: number
}

const _uploadProgress = new Map<string, OutboxUploadProgress>()
const _uploadProgressListeners = new Set<OutboxListener>()

/** Upload progress is page memory only. It changes many times a second, so
 * it stays out of IndexedDB and out of the outbox-change fan-out, whose
 * subscribers re-read IndexedDB. */
export function subscribeOutboxUploadProgress(listener: OutboxListener): () => void {
  _uploadProgressListeners.add(listener)
  return () => { _uploadProgressListeners.delete(listener) }
}

export function outboxUploadProgress(outboxId: string): OutboxUploadProgress | null {
  return _uploadProgress.get(outboxId) ?? null
}

export function outboxUploadPercent(progress: OutboxUploadProgress): number {
  if (progress.totalBytes <= 0) return 0
  return Math.min(100, Math.floor((progress.sentBytes / progress.totalBytes) * 100))
}

function setUploadProgress(outboxId: string, progress: OutboxUploadProgress | null): void {
  const previous = _uploadProgress.get(outboxId)
  if (progress === null) {
    if (!previous) return
    _uploadProgress.delete(outboxId)
  } else {
    // Whole-percent steps are enough for a status label and keep a fast
    // upload from re-rendering the chat on every progress event.
    if (
      previous?.file === progress.file &&
      outboxUploadPercent(previous) === outboxUploadPercent(progress)
    ) return
    _uploadProgress.set(outboxId, progress)
  }
  notifyListeners(_uploadProgressListeners)
}

function db(): Promise<IDBPDatabase<OutboxSchema>> {
  if (_dbPromise) return _dbPromise
  _dbPromise = openDB<OutboxSchema>(DB_NAME, DB_VERSION, {
    upgrade(d: IDBPDatabase<OutboxSchema>) {
      const store = d.createObjectStore('pending', { keyPath: 'id' })
      store.createIndex('by_chat', 'chat_id')
      store.createIndex('by_created', 'created_at')
    },
  })
  return _dbPromise
}

export async function enqueueOutbox(
  chatId: string, body: string, force: boolean = false, topicId: string | null = null,
  attachments: OutboxAttachment[] = [],
  modelOverride: string | null = null,
  effortOverride: string | null = null,
  id: string = newOutboxId(),
  replyToMessageId: string | null = null,
): Promise<string> {
  const d = await db()
  _autoDrainMayHaveWork = true
  await d.put('pending', {
    id, chat_id: chatId, body, force, topic_id: topicId,
    model_override: modelOverride,
    effort_override: effortOverride,
    reply_to_message_id: replyToMessageId,
    attachments,
    created_at: Date.now(), attempts: 0,
  })
  notifyOutboxChanged()
  // The page drain keeps the immediate online path fast. Background Sync is
  // the durable owner once the tab/WebView closes, so a prompt written while
  // offline is sent when connectivity returns without reopening AgentChat.
  void nudgeServiceWorkerOutboxDrain()
  void registerOutboxSync()
  return id
}

export async function nudgeServiceWorkerOutboxDrain(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    const registration = await navigator.serviceWorker.ready
    const worker = navigator.serviceWorker.controller || registration.active
    worker?.postMessage({ type: 'drain-message-outbox' })
  } catch {
    // The sync registration below remains the closed-app retry path.
  }
}

export async function registerOutboxSync(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false
  try {
    const registration = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> }
    }
    if (!registration.sync) return false
    await registration.sync.register(OUTBOX_SYNC_TAG)
    return true
  } catch {
    return false
  }
}

export function newOutboxId(): string {
  return crypto.randomUUID()
}

export async function deleteOutbox(id: string): Promise<void> {
  const d = await db()
  await d.delete('pending', id)
  await refreshAutoDrainHint(d)
  revokeOutboxPreviewUrls(id)
  notifyOutboxChanged()
}

export async function listOutbox(chatId?: string): Promise<OutboxItem[]> {
  const d = await db()
  if (chatId) return d.getAllFromIndex('pending', 'by_chat', chatId)
  return d.getAllFromIndex('pending', 'by_created')
}

type OutboxAttempt = {
  ok: boolean
  item: OutboxItem
  receipt?: OutboxServerReceipt
  error?: string
  status?: number | null
  /** An attachment the server can never accept (too large, rejected
   * payload): retrying would only re-upload the same doomed bytes. */
  permanent?: boolean
}

async function responseError(resp: Response): Promise<string> {
  try {
    const payload = await resp.clone().json() as { detail?: unknown }
    if (typeof payload.detail === 'string' && payload.detail.trim()) return payload.detail.trim()
  } catch {
    // Non-JSON errors still get a concrete HTTP status below.
  }
  return `Server rejected the message (HTTP ${resp.status})`
}

async function _attemptOne(item: OutboxItem): Promise<OutboxAttempt> {
  const d = await db()
  const prepared = await prepareOutboxAttachments(d, item)
  if ('failure' in prepared) {
    return {
      ok: false,
      item: prepared.item,
      error: prepared.failure.error,
      status: prepared.failure.status,
      permanent: prepared.failure.permanent,
    }
  }
  try {
    const body = JSON.stringify({
      text: prepared.item.body,
      force: prepared.item.force,
      topic_id: prepared.item.topic_id,
      model_override: prepared.item.model_override ?? null,
      effort_override: prepared.item.effort_override ?? null,
      client_id: prepared.item.id,
      reply_to_message_id: prepared.item.reply_to_message_id ?? null,
      attachments: prepared.apiAttachments,
    })
    const resp = await fetchWithTimeout(`/api/chats/${item.chat_id}/messages`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
      // keepalive lets an in-flight delivery finish even after the WebView is
      // backgrounded or killed — the drain fires on visibilitychange→hidden as
      // the app leaves the foreground (see startOutboxAutoDrain), so a message
      // sent the instant before a fast app-close still lands. The spec caps a
      // keepalive request body at 64KB, so we only opt in for normal-sized
      // messages; an oversized one falls back to a plain fetch that simply
      // drains on the next foreground tick, which is the pre-existing behaviour.
      keepalive: body.length < 60_000,
    }, OUTBOX_DELIVERY_TIMEOUT_MS)
    if (resp.ok) {
      let receipt: OutboxServerReceipt | undefined
      try {
        const payload = await resp.clone().json() as Partial<OutboxServerReceipt>
        if (typeof payload.message_id === 'string' && typeof payload.queued === 'boolean') {
          receipt = {
            ok: payload.ok !== false,
            message_id: payload.message_id,
            queued: payload.queued,
            session_title: payload.session_title ?? null,
          }
        }
      } catch {
        // A 2xx is still authoritative; the read-model refresh will resolve a
        // malformed or empty response body using the stable client id.
      }
      return { ok: true, item: prepared.item, receipt }
    }
    const error = await responseError(resp)
    return { ok: false, item: prepared.item, error, status: resp.status }
  } catch (err) {
    return {
      ok: false,
      item: prepared.item,
      error: err instanceof Error ? err.message : 'Network delivery failed',
      status: null,
    }
  }
}

/** Statuses that mean the server rejected THIS payload — replaying the
 * identical bytes can never succeed, so retries must change something. */
const PAYLOAD_REJECTION_STATUSES = new Set([400, 404, 422])

/** Remove send-time decoration that can go stale between enqueue and drain:
 * staged upload paths (the attachment store may have been swept) and the
 * composer's model/effort overrides (the model can be retired or the topic
 * can switch provider — e.g. a Codex override enqueued for a topic the
 * server resolves to Claude is rejected with 422 "invalid Claude model").
 * The message text and attachments are the payload Mark actually sent;
 * decoration must never keep them undeliverable. Returns null when there is
 * nothing left to strip. */
export function stripStaleSendDecoration(item: OutboxItem): OutboxItem | null {
  const uploadsReset = resetFileBackedUploads(item)
  const hasOverrides = item.model_override != null || item.effort_override != null
  if (uploadsReset === item && !hasOverrides) return null
  return { ...uploadsReset, model_override: null, effort_override: null }
}

async function attemptAndPersist(
  d: IDBPDatabase<OutboxSchema>,
  item: OutboxItem,
): Promise<OutboxAttempt> {
  const attempt = await _attemptOne(item)
  if (attempt.ok) {
    await d.delete('pending', item.id)
    revokeOutboxPreviewUrls(item.id)
    notifyOutboxChanged()
    return attempt
  }
  // Preserve the user's text, but persist the rejection as an error. This is
  // local recovery work, not a server queue entry. A payload rejection first
  // strips stale decoration so the next attempt sends the message with the
  // topic's own defaults; once nothing is left to strip the item is parked —
  // visible as "Not sent — tap to retry" instead of hammering the server
  // with an identical doomed POST every 30 seconds. A permanent attachment
  // failure parks at once: no decoration change makes that file acceptable.
  const payloadRejected = !attempt.permanent &&
    attempt.status != null && PAYLOAD_REJECTION_STATUSES.has(attempt.status)
  const strippedRetry = payloadRejected ? stripStaleSendDecoration(attempt.item) : null
  const parked = attempt.permanent === true || (payloadRejected && strippedRetry === null)
  await d.put('pending', {
    ...(strippedRetry ?? attempt.item),
    attempts: (attempt.item.attempts ?? 0) + 1,
    last_error: attempt.error || 'Message delivery failed',
    last_status: attempt.status ?? null,
    last_attempt_at: Date.now(),
    parked,
  })
  notifyOutboxChanged()
  if (!parked) void registerOutboxSync()
  return attempt
}

export async function retryOutbox(id: string): Promise<boolean> {
  // A tap-to-retry is a delivery trigger, not a second delivery path. Wait for
  // any current drain, then take the same cross-context lock before POSTing.
  while (_drainPromise) await _drainPromise
  _autoDrainMayHaveWork = true
  _drainPromise = withOutboxDrainLock(async () => {
    const d = await db()
    const item = await d.get('pending', id)
    if (!item) {
      await refreshAutoDrainHint(d)
      return emptyDrainResult()
    }
    const attempt = await attemptAndPersist(d, { ...item, parked: false })
    await refreshAutoDrainHint(d)
    return attempt.ok
      ? {
          sent: 1,
          failed: 0,
          deliveries: [deliveryFromAttempt(attempt)],
        }
      : { sent: 0, failed: 1, deliveries: [] }
  }, true).finally(() => {
    _drainPromise = null
  })
  await _drainPromise
  const d = await db()
  return !(await d.get('pending', id))
}

function emptyDrainResult(): OutboxDrainResult {
  return { sent: 0, failed: 0, deliveries: [] }
}

function deliveryFromAttempt(attempt: OutboxAttempt): OutboxDelivery {
  return {
    client_id: attempt.item.id,
    receipt: attempt.receipt ?? {
      ok: true,
      message_id: attempt.item.id,
      // Preserve a conservative queued UI until the immediate read-model
      // refresh replaces this fallback with the server row.
      queued: true,
    },
  }
}

async function refreshAutoDrainHint(d: IDBPDatabase<OutboxSchema>): Promise<void> {
  const remaining = await d.getAllFromIndex('pending', 'by_created')
  _autoDrainMayHaveWork = remaining.some((item) => !item.parked)
}

async function withOutboxDrainLock(
  run: () => Promise<OutboxDrainResult>,
  waitForOwner: boolean = false,
): Promise<OutboxDrainResult> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks?.request) return run()
  return locks.request(
    OUTBOX_DRAIN_LOCK,
    waitForOwner
      ? { mode: 'exclusive' }
      : { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (!lock) {
        // Another page/service-worker context owns delivery. Keep a future
        // closed-app wake armed instead of issuing the same POST concurrently.
        void registerOutboxSync()
        return emptyDrainResult()
      }
      return run()
    },
  )
}

export async function drainOutbox(): Promise<OutboxDrainResult> {
  if (_drainPromise) return _drainPromise
  _drainPromise = withOutboxDrainLock(async () => {
    const d = await db()
    const items = await d.getAllFromIndex('pending', 'by_created')
    let sent = 0
    let failed = 0
    const deliveries: OutboxDelivery[] = []
    for (const item of items) {
      if (item.parked) continue
      const attempt = await attemptAndPersist(d, item)
      if (attempt.ok) {
        sent++
        deliveries.push(deliveryFromAttempt(attempt))
      } else {
        failed++
      }
    }
    await refreshAutoDrainHint(d)
    return { sent, failed, deliveries }
  }).finally(() => {
    _drainPromise = null
  })
  return _drainPromise
}

/** A real delivery opportunity. navigator.onLine is deliberately not a gate:
 * Android and Tailscale can restore this private origin without changing that
 * coarse browser hint. Failed fetches remain durable and re-arm Background
 * Sync; a WebSocket open proves the origin is reachable and calls this too. */
export function signalOutboxDeliveryOpportunity(): void {
  if (!_autoDrainMayHaveWork) return
  const alreadyDraining = _drainPromise !== null
  void drainOutbox()
  if (!alreadyDraining) void nudgeServiceWorkerOutboxDrain()
}

/** Any successful same-origin API response is direct proof that the private
 * AgentChat route has recovered. This catches the exact Android case where
 * REST reads work again but neither `online` nor WebSocket-open fires yet. */
export function noteAgentChatOriginReachable(): void {
  signalOutboxDeliveryOpportunity()
}

export function startOutboxAutoDrain() {
  ensureOutboxNotifications()
  // Cold-boot drain.
  signalOutboxDeliveryOpportunity()
  // Drain on reconnect.
  window.addEventListener('online', signalOutboxDeliveryOpportunity)
  // Drain whenever the app crosses the foreground boundary. visibilitychange
  // is the canonical "app reopened" signal on the Android APK (the same one
  // agentBannerWatchdog / ChatPage / ApkUpdateLauncher rely on), and it also
  // fires as the app backgrounds, so a single listener covers both halves of
  // the "send then immediately close" gap:
  //   - hidden  → flush a just-sent message as the app leaves the foreground;
  //               the keepalive POST in _attemptOne lets it complete after the
  //               WebView is hidden.
  //   - visible → re-attempt delivery the instant the app returns, instead of
  //               waiting up to 30s — Android freezes the timer below while
  //               the app is backgrounded, so previously an undelivered message
  //               sat queued until the next cold boot.
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', signalOutboxDeliveryOpportunity)
  }
  // Bounded fallback while the page is alive. The WebSocket open path is the
  // immediate reachability signal; this covers a socket-disabled/zombied page.
  setInterval(signalOutboxDeliveryOpportunity, 15_000)
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  if (typeof AbortController === 'undefined') return fetch(input, init)
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } finally {
    clearTimeout(timer)
  }
}

type UploadResponse = {
  id?: unknown
  kind?: unknown
  filename?: unknown
  mime_type?: unknown
  size_bytes?: unknown
  local_path?: unknown
}

function isUploadedAttachment(att: OutboxAttachment): att is OutboxAttachment & { local_path: string } {
  return typeof att.local_path === 'string' && att.local_path.length > 0
}

function apiAttachment(att: OutboxAttachment & { local_path: string }): Omit<OutboxAttachment, 'file' | 'preview_url'> {
  return {
    id: att.id,
    kind: att.kind,
    filename: att.filename,
    mime_type: att.mime_type,
    size_bytes: att.size_bytes,
    local_path: att.local_path,
  }
}

function uploadFilename(att: OutboxAttachment): string {
  const name = att.filename?.trim()
  if (name) return name
  const extension =
    att.mime_type === 'image/jpeg' ? 'jpg' :
    att.mime_type === 'image/png' ? 'png' :
    att.mime_type === 'image/webp' ? 'webp' :
    att.mime_type === 'image/gif' ? 'gif' :
    att.kind === 'pdf' ? 'pdf' :
    'bin'
  return `agentchat-attachment-${att.id}.${extension}`
}

type UploadFailure = {
  error: string
  status: number | null
  permanent: boolean
}

type UploadOutcome = { attachment: OutboxAttachment } | { failure: UploadFailure }

function formatUploadBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${+(bytes / 1024 ** 3).toFixed(1)} GB`
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`
}

function tooLargeFailure(att: OutboxAttachment, sizeBytes: number): UploadFailure {
  return {
    error: `${uploadFilename(att)} is too big to send (${formatUploadBytes(sizeBytes)}; ` +
      `limit ${formatUploadBytes(MAX_UPLOAD_BYTES)})`,
    status: 413,
    permanent: true,
  }
}

type ChunkResult = { status: number; body: string } | 'stalled' | 'network'

/** PUT one chunk with XMLHttpRequest: fetch cannot report upload progress,
 * and progress is what separates a slow upload from a dead one. */
function putUploadChunk(
  url: string,
  body: Blob,
  onProgress: (sentBytes: number) => void,
): Promise<ChunkResult> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    let stallTimer: ReturnType<typeof setTimeout> | undefined
    let stalled = false
    const settle = (result: ChunkResult) => {
      clearTimeout(stallTimer)
      resolve(result)
    }
    // Re-armed by every progress event, and once more when the body is sent
    // so the server gets the same window to store the bytes and answer.
    const armStallTimer = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => {
        stalled = true
        xhr.abort()
      }, UPLOAD_STALL_TIMEOUT_MS)
    }
    try {
      xhr.open('PUT', url)
      xhr.withCredentials = true
      xhr.upload.onprogress = (event) => {
        armStallTimer()
        onProgress(event.loaded)
      }
      xhr.upload.onload = armStallTimer
      xhr.onload = () => settle({ status: xhr.status, body: xhr.responseText })
      xhr.onerror = () => settle('network')
      xhr.onabort = () => settle(stalled ? 'stalled' : 'network')
      armStallTimer()
      xhr.send(body)
    } catch {
      settle('network')
    }
  })
}

type ResumableState = UploadResponse & { received?: unknown; complete?: UploadResponse }

function parseUploadJson(text: string): ResumableState {
  try {
    const value: unknown = JSON.parse(text)
    return value && typeof value === 'object' ? value as ResumableState : {}
  } catch {
    return {}
  }
}

function receiptAttachment(att: OutboxAttachment, uploaded: UploadResponse): OutboxAttachment | null {
  const localPath = typeof uploaded.local_path === 'string' ? uploaded.local_path : ''
  const uploadedId = typeof uploaded.id === 'string' ? uploaded.id : ''
  if (!localPath || !uploadedId) return null
  return {
    id: uploadedId,
    kind: (
      uploaded.kind === 'image' ||
      uploaded.kind === 'video' ||
      uploaded.kind === 'audio' ||
      uploaded.kind === 'voice' ||
      uploaded.kind === 'document' ||
      uploaded.kind === 'pdf'
    ) ? uploaded.kind : att.kind,
    filename: typeof uploaded.filename === 'string' ? uploaded.filename : att.filename,
    mime_type: typeof uploaded.mime_type === 'string' ? uploaded.mime_type : att.mime_type,
    size_bytes: typeof uploaded.size_bytes === 'number' ? uploaded.size_bytes : att.size_bytes,
    local_path: localPath,
    preview_url: att.preview_url,
    file: att.file,
  }
}

function rejectedFailure(name: string, status: number): UploadFailure {
  return {
    error: `Upload of ${name} was rejected (HTTP ${status})`,
    status,
    // A 4xx about this exact file or chat cannot change on replay; auth,
    // timeout, rate-limit and resumable-offset (409) answers can.
    permanent: status >= 400 && status < 500 && ![401, 403, 408, 409, 429].includes(status),
  }
}

/** Upload one file resumably: ask the server how many bytes it already holds
 * for this attachment, then PUT the rest in chunks from exactly that offset.
 * An interrupted upload (Android froze the app, the connection dropped)
 * therefore continues where it stopped instead of restarting the file.
 * Transient failures carry a short reason; the caller words the message. */
async function uploadOutboxAttachment(
  chatId: string,
  att: OutboxAttachment,
  onProgress: (sentBytes: number) => void,
): Promise<UploadOutcome> {
  const name = uploadFilename(att)
  const file = att.file
  if (!file) {
    return { failure: { error: `${name} is no longer saved on this device`, status: null, permanent: true } }
  }
  const lost: UploadOutcome = { failure: { error: 'connection lost', status: null, permanent: false } }
  const base = `/api/chats/${encodeURIComponent(chatId)}/resumable-uploads/${encodeURIComponent(att.id)}`
  let received: number
  try {
    const resp = await fetchWithTimeout(base, { credentials: 'include' }, OUTBOX_DELIVERY_TIMEOUT_MS)
    if (!resp.ok) return { failure: rejectedFailure(name, resp.status) }
    const state = parseUploadJson(await resp.text())
    const finished = state.complete ? receiptAttachment(att, state.complete) : null
    if (finished) return { attachment: finished }
    received = typeof state.received === 'number' ? state.received : 0
  } catch {
    return lost
  }
  const params = `&total=${file.size}&filename=${encodeURIComponent(name)}` +
    `&mime_type=${encodeURIComponent(att.mime_type || file.type || '')}`
  let resyncs = 0
  for (;;) {
    const offset = received
    const result = await putUploadChunk(
      `${base}?offset=${offset}${params}`,
      file.slice(offset, Math.min(file.size, offset + RESUMABLE_CHUNK_BYTES)),
      (loaded) => onProgress(offset + loaded),
    ).catch(() => 'network' as const)
    if (result === 'stalled') {
      return { failure: { error: 'no progress for 60 seconds', status: null, permanent: false } }
    }
    if (result === 'network') return lost
    const reply = parseUploadJson(result.body)
    if (result.status === 409 && typeof reply.received === 'number' && resyncs < 3) {
      // The server holds a different number of bytes: continue from its count.
      resyncs += 1
      received = reply.received
      continue
    }
    if (result.status === 413) return { failure: tooLargeFailure(att, file.size) }
    if (result.status < 200 || result.status >= 300) {
      const failure = rejectedFailure(name, result.status)
      return { failure: failure.permanent ? failure : { ...failure, error: `server answered HTTP ${result.status}` } }
    }
    const finished = receiptAttachment(att, reply)
    if (finished) return { attachment: finished }
    if (typeof reply.received !== 'number' || reply.received <= offset) {
      return { failure: { error: 'no server receipt', status: result.status, permanent: false } }
    }
    resyncs = 0
    received = reply.received
  }
}

type ScreenWakeLock = { release: () => Promise<void> }

/** Keep the screen on while files upload. A phone that dims and locks
 * freezes AgentChat, which pauses the upload until it is reopened. The lock
 * drops by itself whenever the app leaves the screen. */
async function keepScreenAwake(): Promise<() => void> {
  const wakeLock = typeof navigator === 'undefined'
    ? undefined
    : (navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<ScreenWakeLock> } }).wakeLock
  if (!wakeLock) return () => {}
  try {
    const sentinel = await wakeLock.request('screen')
    return () => { void sentinel.release().catch(() => {}) }
  } catch {
    // Not visible, not allowed, or unsupported: uploads work without it.
    return () => {}
  }
}

function resetFileBackedUploads(item: OutboxItem): OutboxItem {
  let changed = false
  const attachments = (item.attachments ?? []).map((att) => {
    if (!att.file || !isUploadedAttachment(att)) return att
    changed = true
    return { ...att, local_path: null }
  })
  return changed ? { ...item, attachments } : item
}

async function prepareOutboxAttachments(
  d: IDBPDatabase<OutboxSchema>,
  item: OutboxItem,
): Promise<
  | { item: OutboxItem; apiAttachments: ReturnType<typeof apiAttachment>[] }
  | { item: OutboxItem; failure: UploadFailure }
> {
  const attachments = [...(item.attachments ?? [])]
  // Refuse an oversized file before any byte of the message is sent, rather
  // than after the files ahead of it have used up mobile data.
  for (const att of attachments) {
    if (att && !isUploadedAttachment(att) && att.file && att.file.size > MAX_UPLOAD_BYTES) {
      return { item, failure: tooLargeFailure(att, att.file.size) }
    }
  }
  const sizeOf = (att: OutboxAttachment | undefined) => att?.size_bytes ?? att?.file?.size ?? 0
  const totalBytes = attachments.reduce((sum, att) => sum + sizeOf(att), 0)
  let storedBytes = attachments
    .filter(isUploadedAttachment)
    .reduce((sum, att) => sum + sizeOf(att), 0)
  let currentItem = item
  const releaseScreen = attachments.some((att) => att && !isUploadedAttachment(att))
    ? await keepScreenAwake()
    : () => {}
  try {
    for (let i = 0; i < attachments.length; i += 1) {
      const att = attachments[i]
      if (!att) continue
      if (isUploadedAttachment(att)) continue

      const reportProgress = (sentBytes: number) => setUploadProgress(item.id, {
        file: i + 1,
        files: attachments.length,
        sentBytes: storedBytes + Math.min(sentBytes, sizeOf(att)),
        totalBytes,
      })
      reportProgress(0)
      const outcome = await uploadOutboxAttachment(item.chat_id, att, reportProgress)
      if ('failure' in outcome) {
        const failure = outcome.failure
        return {
          item: currentItem,
          failure: failure.permanent ? failure : {
            ...failure,
            error: `paused at file ${i + 1} of ${attachments.length} (${failure.error}); resumes automatically`,
          },
        }
      }
      storedBytes += sizeOf(att)
      attachments[i] = outcome.attachment
      currentItem = { ...currentItem, attachments }
      // Each stored file is persisted, so a retry resumes at the next one.
      await d.put('pending', currentItem)
    }
  } finally {
    releaseScreen()
    setUploadProgress(item.id, null)
  }

  const apiAttachments = attachments
    .filter(isUploadedAttachment)
    .map(apiAttachment)

  if (attachments.length > 0 && apiAttachments.length !== attachments.length) {
    return {
      item: currentItem,
      failure: { error: 'An attachment could not be prepared for upload', status: null, permanent: false },
    }
  }
  return { item: currentItem, apiAttachments }
}

function previewKey(outboxId: string, att: OutboxAttachment): string {
  return `${outboxId}:${att.id}:${att.filename ?? ''}:${att.size_bytes ?? ''}`
}

export function outboxAttachmentPreviewUrl(item: OutboxItem, att: OutboxAttachment): string | null {
  if (att.preview_url && !att.preview_url.startsWith('blob:')) return att.preview_url
  if (!att.file) return null
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null
  const key = previewKey(item.id, att)
  const existing = _previewUrls.get(key)
  if (existing) return existing
  const url = URL.createObjectURL(att.file)
  _previewUrls.set(key, url)
  return url
}

function revokeOutboxPreviewUrls(outboxId: string): void {
  for (const [key, url] of _previewUrls.entries()) {
    if (!key.startsWith(`${outboxId}:`)) continue
    URL.revokeObjectURL(url)
    _previewUrls.delete(key)
  }
}
